import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { images, galleryItems } from "../data/siteData";
import { PageHero } from "../components/PageHero";
import { Lightbox } from "../components/Lightbox";
import { Reveal } from "../components/Reveal";

const filters = ["All", "Food", "Desserts", "Ambience", "Dining", "Celebrations"];

export default function Gallery() {
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    document.title = "Gallery | Mango – World Cuisine Restaurant, Bhavnagar";
  }, []);

  const visible = useMemo(
    () => (filter === "All" ? galleryItems : galleryItems.filter((g) => g.category === filter)),
    [filter]
  );

  return (
    <>
      <PageHero
        overline="Gallery"
        lines={["A feast", "for the eyes."]}
        image={images.heroGallery}
        description="Food, desserts, sizzlers, ambience and celebrations at Mango."
      />

      <section data-testid="gallery-page" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 -mx-6 px-6 md:mx-0 md:px-0 md:flex-wrap">
              {filters.map((f) => (
                <button
                  key={f}
                  data-testid={`gallery-filter-${f.toLowerCase()}`}
                  onClick={() => { setFilter(f); setLightbox(null); }}
                  aria-pressed={filter === f}
                  className={`shrink-0 rounded-full px-5 py-2.5 text-xs font-semibold tracking-wide transition-colors duration-300 ${
                    filter === f
                      ? "bg-forest text-ivory"
                      : "bg-sand text-charcoal/70 hover:bg-forest/10 hover:text-forest"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </Reveal>

          <motion.div layout className="mt-12 columns-2 md:columns-3 gap-5 [&>*]:mb-5">
            <AnimatePresence mode="popLayout">
              {visible.map((item, i) => (
                <motion.div
                  layout
                  key={item.src}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="break-inside-avoid"
                >
                  <button
                    data-testid={`gallery-page-thumb-${i}`}
                    onClick={() => setLightbox(i)}
                    aria-label={`Open image: ${item.alt}`}
                    className="group relative block w-full overflow-hidden rounded-2xl"
                  >
                    <img
                      src={item.src}
                      alt={item.alt}
                      loading="lazy"
                      className="w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <span className="absolute inset-0 bg-forest/0 transition-colors duration-500 group-hover:bg-forest/25" />
                    <span className="absolute bottom-4 left-4 rounded-full bg-ivory/90 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-forest opacity-0 translate-y-2 transition-[opacity,transform] duration-400 group-hover:opacity-100 group-hover:translate-y-0">
                      {item.category}
                    </span>
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <Lightbox items={visible} index={lightbox} onClose={() => setLightbox(null)} onNavigate={setLightbox} />
    </>
  );
}
